import React, { memo, useState } from 'react';
import { Stack, Icon, Menu, MenuItem, ListItemIcon, ListItemText, Divider, LoadingButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from 'investira.react.components';
import { ASSINATURA_ESTAGIO } from '../../../enums';
import useAssinatura from '../../../hooks/useAssinatura';
import useAcesso from '../../../hooks/useAcesso';
import withMessage from '../../../hoc/withMessage';
import services from '../../../services';
const xAssinaturaEstagiosOrdenados = Object.values(ASSINATURA_ESTAGIO).sort((xLeft, xRight) => xLeft.valor - xRight.valor);
const xAssinaturaEstagioPorValor = xAssinaturaEstagiosOrdenados.reduce((xAccumulator, xItem) => {
  xAccumulator[xItem.valor] = xItem;
  return xAccumulator;
}, {});

// TODO:
// [] Recuperar os id(s) do recurso desse array ou objeto
// [] Importar serviço de patch da assinatura

/**
 * Renderiza o componente Assinar.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {*} props.recurso - Propriedade `recurso` do componente.
 * @param {*} props.disabled - Propriedade `disabled` do componente.
 * @param {*} props.size - Propriedade `size` do componente.
 * @param {*} props.data - Propriedade `data` do componente.
 * @param {*} props.refreshData - Propriedade `refreshData` do componente.
 * @param {*} props.onMessage - Propriedade `onMessage` do componente.
 * @param {*} props.onMessageError - Propriedade `onMessageError` do componente.
 *
 * @description
 * Este componente realiza as seguintes operacoes:
 * 1. Recebe as propriedades necessarias para compor a interface
 * 2. Organiza os estados e callbacks usados durante a renderizacao
 * 3. Renderiza a experiencia visual do organismo
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const Assinar = memo(props => {
  const [isSubmitting, setIsSubmitting] = useState();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmacao, setConfirmacao] = useState({
    payload: [],
    estagioId: null,
    actLabel: ''
  });
  const acesso = useAcesso();
  const assinatura = useAssinatura();
  const {
    podeAssinar
  } = acesso;
  const {
    usuario_id,
    getAssinaturas,
    getAssinaturasTipo,
    estagiosAssinaturaDisponiveis
  } = assinatura;
  const {
    recurso,
    disabled = false,
    size = 'medium',
    data,
    refreshData
  } = props;

  // State do menu - segue padrão de nomeação exigido: anchorEl
  const [anchorEl, setAnchorEl] = useState(null);

  // Verifica se algum item foi criado pelo próprio usuário e impede a ação

  function getEstagioValor(pEstagio) {
    if (typeof pEstagio === 'number') {
      return pEstagio;
    }
    if (typeof pEstagio === 'string') {
      if (ASSINATURA_ESTAGIO[pEstagio]) {
        return ASSINATURA_ESTAGIO[pEstagio].valor;
      }
      const xParsed = Number(pEstagio);
      if (!Number.isNaN(xParsed) && xAssinaturaEstagioPorValor[xParsed]) {
        return xParsed;
      }
    }
    return null;
  }
  function verifyRegistroProprio(pArray, pUsuarioId) {
    const xUsuarioIdNormalizado = Number(pUsuarioId);
    if (Number.isNaN(xUsuarioIdNormalizado)) {
      return false;
    }
    const xPossuiProprioRegistro = pArray.some(xItem => Number(xItem.usuario_id) === xUsuarioIdNormalizado);
    return xPossuiProprioRegistro;
  }
  function verifyEstagio(pArray, pEstagioId) {
    const xEstagioValor = getEstagioValor(pEstagioId);
    if (xEstagioValor === null) {
      return false;
    }
    const xPossuiRegistroAcima = pArray.some(xItem => getEstagioValor(xItem.assinatura_estagio) > xEstagioValor);
    return xPossuiRegistroAcima;
  }
  function registrosValidos(pArray, pEstagioId) {
    // Caso o estágio seja RG, retorna apenas registros que não tenham RG, pois trata-se de uma reprovação
    if (pEstagioId === ASSINATURA_ESTAGIO.RG.id) {
      return pArray.filter(xItem => {
        return xItem.assinatura_estagio !== null && xItem.assinatura_estagio !== ASSINATURA_ESTAGIO.RG.id;
      });
    }
    const xEstagioValor = getEstagioValor(pEstagioId);

    // Registros válidos para a ação: apenas onde o estágio atual é menor ou igual ao estágio solicitado
    // (ou seja, não permitir ação em registros já em estágio mais avançado)
    const xValidos = pArray.filter(xItem => {
      const xItemEstagioValor = getEstagioValor(xItem.assinatura_estagio);
      return xItemEstagioValor !== null && xItemEstagioValor < xEstagioValor;
    } // caso assinatura_estagio não reconhecido, permitir
    );
    return xValidos;
  }
  const assinarRegistros = (pArray, pEstagioId, pUsuarioId) => {
    if (!Array.isArray(pArray) || !pArray.length) {
      return [];
    }
    if (verifyRegistroProprio(pArray, pUsuarioId)) {
      props.onMessage && props.onMessageError('Você não pode assinar registros criados por você mesmo. Selecione apenas registros criados por outros usuários.');
      return [];
    }

    // Verifica se algum registro está apenas um estágio acima do solicitado e impede a ação
    // A não ser que seja reprovação

    if (verifyEstagio(pArray, pEstagioId) && pEstagioId !== 'RG') {
      props.onMessage && props.onMessageError(`Não é possível assinar registros que já possuem estágio superior. 
          Selecione apenas registros cujo estágio atual permita a ação. 
          Caso precise retornar, use a ação de reprovar.`);
      return [];
    }

    // Remove registros no mesmo estágio da assinatura antes de enviar
    const xPayloadValido = registrosValidos(pArray, pEstagioId);

    // Gera payload para cada registro selecionado
    const xPayload = xPayloadValido.map(xItem => ({
      ...xItem,
      output_tipo: recurso,
      assinatura_estagio: ASSINATURA_ESTAGIO[pEstagioId].id
    }));
    return xPayload;
  };
  function assinarSubmit(pPayload) {
    setIsSubmitting(true);
    services.outputs.assinar(pPayload, () => {
      refreshData && refreshData();
    }, rErr => {
      props.onMessage && props.onMessageError(rErr.description);
    }, () => {
      setIsSubmitting(false);
    });
  }

  // Handlers do menu
  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleCloseConfirmDialog = () => {
    if (isSubmitting) {
      return;
    }
    setIsConfirmDialogOpen(false);
    setConfirmacao({
      payload: [],
      estagioId: null,
      actLabel: ''
    });
  };
  const handleOpenConfirmDialog = (pPayload, pEstagioId) => {
    if (!Array.isArray(pPayload) || !pPayload.length) {
      props.onMessageError('Os registros já estão no estágio selecionado ou acima.');
      return;
    }
    const xActLabel = ASSINATURA_ESTAGIO[pEstagioId]?.actLabel || 'Confirmar ação';
    setConfirmacao({
      payload: pPayload,
      estagioId: pEstagioId,
      actLabel: xActLabel
    });
    setIsConfirmDialogOpen(true);
  };
  const handleConfirmAssinarSubmit = () => {
    if (!confirmacao.payload.length) {
      return;
    }
    assinarSubmit(confirmacao.payload);
    setIsConfirmDialogOpen(false);
  };
  const isReprovarConfirmacao = confirmacao.estagioId === 'RG';
  const confirmDialogTitle = isReprovarConfirmacao ? 'Reprovar' : confirmacao.actLabel;
  const confirmDialogMessage = isReprovarConfirmacao ? `Você está prestes a reprovar ${confirmacao.payload.length} registro(s). Ao confirmar, o registro retornará para o estágio inicial de "Registrado". Deseja continuar?` : `Aplicar esta ação em ${confirmacao.payload.length} registro(s). Deseja continuar?`;

  // Stubs para ações dos itens
  const handleReprovar = (pData, pUsuarioId) => {
    const xPayload = assinarRegistros(pData, 'RG', pUsuarioId);
    handleOpenConfirmDialog(xPayload, 'RG');
    setAnchorEl(null);
  };
  const handleAssinar = (pData, pEstagioId, pUsuarioId) => {
    const xPayload = assinarRegistros(pData, pEstagioId, pUsuarioId);
    handleOpenConfirmDialog(xPayload, pEstagioId);
    setAnchorEl(null);
  };

  // TODO: Trocar pela prop recurso
  if (!podeAssinar(recurso)) {
    return null;
  }
  const xAssinatura = getAssinaturas();
  const xAssinaturasTipo = getAssinaturasTipo(xAssinatura[recurso]);
  const xAssinaturasTipoDisponiveis = estagiosAssinaturaDisponiveis.filter(xItem => xItem.id !== ASSINATURA_ESTAGIO.RG.id && xAssinaturasTipo[xItem.id]);
  return <Stack direction="row" spacing={1.5}>
      <Menu id="assinar-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left'
    }} transformOrigin={{
      vertical: 'top',
      horizontal: 'left'
    }}>
        {xAssinaturasTipoDisponiveis.map(xItem => {
        const {
          id: xEstagioId,
          actLabel,
          icon,
          iconColor
        } = xItem;
        return <MenuItem key={xEstagioId} onClick={() => handleAssinar(data, xEstagioId, usuario_id)} disabled={disabled} dense>
              <ListItemIcon>
                <Icon iconName={icon} size={16} color={iconColor} />
              </ListItemIcon>
              <ListItemText>{actLabel}</ListItemText>
            </MenuItem>;
      })}

        {xAssinatura[recurso] > 128 && <Divider />}
        <MenuItem onClick={() => handleReprovar(data, usuario_id)} disabled={disabled} dense>
          <ListItemIcon>
            <Icon iconName="sign_prohibid" size={16} color="error" />
          </ListItemIcon>
          <ListItemText color="error">Reprovar</ListItemText>
        </MenuItem>
      </Menu>

      <LoadingButton variant="contained" color="primary" disabled={disabled || xAssinaturasTipoDisponiveis.length === 0} size={size} sx={{
      px: 2
    }} onClick={handleMenuClick} startIcon={<Icon iconName={'stamp'} size={16} />} loading={isSubmitting}>
        Assinar
      </LoadingButton>

      <Dialog open={isConfirmDialogOpen} onClose={handleCloseConfirmDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmDialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} variant="outlined" disabled={isSubmitting}>
            Ainda não
          </Button>
          <LoadingButton onClick={handleConfirmAssinarSubmit} variant="contained" loading={isSubmitting} autoFocus>
            Confirmar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>;
});
export default withMessage(Assinar);
